<template>
  <div class="organization container-fluid">
    <div class="row header-row">
      <div class="col-12 col-md-1">
        <h1>Users</h1>
      </div>
      <div class="col-12 col-md-11">
        <form-component>
          <form
            novalidate="novalidate"
            class="row"
            @submit.prevent
          >
            <div class="col-12 col-md-3 ">
              <input-select
                v-if="$store.getters.isKgbAdminUser
                  && organizationSelectOptions
                  && organizationSelectOptions.length > 0"
                v-model="selectedOrg"
                data-vv-name="Organization Filter"
                label="Organization Filter"
                :options="organizationSelectOptions"
                key-field="id"
                display-field="name"
                @change="onFilterChange"
              />
            </div>
            <div class="col-12 col-md-3 ">
              <input-select
                v-if="siteSelectOptions && siteSelectOptions.length > 0"
                v-model="selectedSite"
                label="Site Filter"
                :options="siteSelectOptions"
                key-field="id"
                display-field="name"
                @change="onSiteChange"
              />
            </div>
            <div class="col-12 col-md-3 ">
              <input-select
                v-model="selectedUserType"
                label="User Type Filter"
                :options="userTypesFilter"
                key-field="id"
                display-field="name"
                @change="onTypeChange"
              />
            </div>
            <div class="col-12 col-md-3 ">
              <input-text
                id="search"
                v-model="search"
                name="search"
                data-vv-name="search"
                data-vv-as="Search"
                label="Search"
                type="search"
              />
            </div>
          </form>
        </form-component>

        <button
          v-if="$store.getters.isAdminUser"
          class="btn btn--primary"
          type="button"
          @click="showCreateModal()"
        >
          <font-awesome-icon
            :icon="['fas', 'plus']"
            class="icon alt"
          />
          New User
        </button>

        <save-user-modal
          ref="userModal"
          :do-cancel="closeModal"
          :do-save="doSaveUser"
          :do-delete-public-key="deletePublicAccessKey"
          :get-organization-sites="getOrganizationSites"
        />
      </div>
    </div>
    <div class="row">
      <div
        v-if="!users"
        class="col-12"
      >
        <Loader />
      </div>
      <div
        v-else-if="filteredUsers.length === 0"
        class="col-12"
      >
        <div class="no-results text-center">
          <div class="vertical-center">
            No Results Found
          </div>
        </div>
      </div>
      <div
        v-else
        class="col-12"
      >
        <div>
          <responsive-table>
            <user-table
              :users="filteredUsers"
              :show-edit-modal="showEditModal"
              :show-reset-user="showResetUser"
              :delete-user="deleteUser"
              :reset-mfa="resetMfa"
            />
          </responsive-table>
        </div>
      </div>
    </div>

    <confirm-modal
      ref="confirmModal"
      :title="confirmTitle"
      :body="confirmBody"
      :on-confirm="confirmAction"
    />
  </div>
</template>

<script>
/* eslint-disable camelcase */
import FormComponent from '@/components/FormComponent.vue';
import Loader from '@/components/Loader.vue';
import ResponsiveTable from '@/components/ResponsiveTable.vue';
import InputSelect from '@/components/inputs/InputSelect.vue';
import InputText from '@/components/inputs/InputText.vue';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import SaveUserModal from '@/components/modals/SaveUserModal.vue';
import UserTable from '@/components/tables/UserTable.vue';

import UserClient from '@/api/UserClient';

export default {
  name: 'Users',
  components: {
    ResponsiveTable,
    UserTable,
    SaveUserModal,
    Loader,
    ConfirmModal,
    FormComponent,
    InputText,
    InputSelect,
  },
  data() {
    return {
      confirmTitle: '',
      confirmBody: '',
      search: '',
      confirmAction: () => {},

      userClient: new UserClient(this.$http),
      users: [],
      organizations: [],
      selectedOrg: 'all',
      sites: [],
      selectedSite: 'all',
      selectedUser: null,
      selectedUserType: 'all',
      userTypesFilter: [
        { id: 'all', name: 'All Types' },
        { id: 'admin', name: 'Admin' },
        { id: 'orgAdmin', name: 'Organization Admin' },
        { id: 'siteAdmin', name: 'Site Admin' },
        { id: 'read', name: 'Read-Only' },
      ],
    };
  },
  computed: {
    organizationSelectOptions() {
      const options = [{ id: 'all', name: 'All Organizations' }, { id: 'admin', name: 'Admin Users' }];
      if (!this.organizations) {
        return [];
      }
      return [...options,
        ...this.organizations
          .filter(org => !org.is_removed)
          .map(org => ({
            id: `${org.id}`,
            name: org.name,
          }))];
    },
    siteSelectOptions() {
      const options = [{ id: 'all', name: 'All Sites' }, { id: '-1', name: 'Primary' }];
      if (!this.sites || this.sites.length === 0) {
        return [];
      }
      return [...options, ...this.sites.map(site => ({ ...site, id: `${site.id}` }))];
    },
    filteredUsers() {
      if (this.users) {
        return this.users
          .filter(x => x.full_name.toLowerCase().includes(this.search.toLowerCase())
            && (this.selectedOrg === 'all'
                || (this.selectedOrg === 'admin' && x.organization_id === null)
                || (x.organization_id && this.selectedOrg === x.organization_id.toString()))
            && (this.selectedSite === 'all'
                || (this.selectedSite === '-1' && x.sites && x.sites.length === 0)
                || (x.sites && x.sites.some(site => site.id === Number(this.selectedSite))))
            && (this.selectedUserType === 'all'
                || (this.selectedUserType === 'admin' && x.is_admin)
                || (this.selectedUserType === 'orgAdmin' && x.is_organization_admin)
                || (this.selectedUserType === 'siteAdmin' && x.is_site_admin && !x.is_organization_admin)
                || (this.selectedUserType === 'read' && !x.is_admin && !x.is_organization_admin && !x.is_site_admin)
            ));
      }
      return null;
    },
  },
  mounted() {
    this.getUsers();
    this.getOrganizations();
    this.selectedOrg = this.$route.params.organization_id
      || this.$route.query.org ? this.$route.query.org : 'all';
    if (this.selectedOrg !== 'all' || !this.$store.getters.isKgbAdminUser) {
      this.getSites();
    }
    this.selectedSite = this.$route.query.site ? this.$route.query.site : 'all';
    this.selectedUserType = this.$route.query.userType ? this.$route.query.userType : 'all';
    this.userTypes = this.userTypes
      .filter(x => (this.$store.getters.isKgbAdminUser || x.id !== 'admin')
          && (this.$store.getters.isOrgAdminAccess || x.id !== 'orgAdmin'));
  },

  methods: {
    getUsers() {
      this.users = null;
      this.userClient.getUsers().then((users) => {
        this.users = users;
      }).catch(() => {
        this.users = [];
      });
    },
    async getSites() {
      this.sites = null;
      const organizationFilter = (!this.$store.getters.isKgbAdminUser)
        ? this.$store.getters.currentUser.organization_id
        : this.selectedOrg;
      if (organizationFilter === 'all') return;
      this.sites = await this.userClient.getSitesForOrganization(organizationFilter);
    },
    onFilterChange() {
      this.selectedSite = 'all';
      this.getSites();
      this.$router.push({
        query: {
          ...this.$route.query, org: this.selectedOrg === 'all' ? undefined : this.selectedOrg, site: undefined, p: 1,
        },
      });
    },
    onSiteChange() {
      this.$router.push({ query: { ...this.$route.query, site: this.selectedSite === 'all' ? undefined : this.selectedSite, p: 1 } });
    },
    onTypeChange() {
      this.$router.push({ query: { ...this.$route.query, userType: this.selectedUserType === 'all' ? undefined : this.selectedUserType, p: 1 } });
    },
    getOrganizations() {
      this.organizations = null;
      this.userClient.getOrganizations().then((orgs) => {
        orgs.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
        this.organizations = orgs;
      }).catch(() => {
        this.organizations = [];
      });
    },

    showCreateModal() {
      this.$refs.userModal.$children[0].show();
      this.$refs.userModal.setModal({}, this.organizations);
    },
    showEditModal(user) {
      this.$refs.userModal.$children[0].show();
      this.$refs.userModal.setModal(user, this.organizations);
      if (user.allowed_programmatic_access) {
        this.userClient.getUserPublicKeysForUser(user.id).then((publicKeys) => {
          this.$refs.userModal.setProgrammaticAccessPublicKeys(publicKeys);
        });
      }
    },
    closeModal() {
      this.$refs.userModal.$children[0].hide();
    },
    doSaveUser(id, email, is_admin, full_name, phone_number,
      phone_country_code, phone_number_e164,
      is_organization_admin, organization_id, allowed_to_remove_files,
      allowed_to_access_management, allowed_programmatic_access, is_site_admin,
      sites,
      whenDone) {
      if (id) {
        this.userClient
          .updateUser(id, email, is_admin, full_name, phone_number, phone_country_code,
            phone_number_e164, is_organization_admin, organization_id, allowed_to_remove_files,
            allowed_to_access_management, allowed_programmatic_access, is_site_admin, sites)
          .then(() => {
            this.closeModal();
            this.getUsers();

            this.$store.commit('setGlobalAlert', {
              msg: 'User saved!',
            });
          }).catch(() => {
            this.$store.commit('setGlobalAlert', {
              msg: 'Unable to save user. Please try again!', error: true,
            });
            whenDone();
          });
      } else {
        this.userClient
          .inviteUser(email, is_admin, full_name, phone_number, phone_country_code,
            phone_number_e164, is_organization_admin, organization_id,
            allowed_to_remove_files, allowed_to_access_management, allowed_programmatic_access,
            is_site_admin, sites)
          .then(() => {
            this.closeModal();
            this.getUsers();

            this.$store.commit('setGlobalAlert', {
              msg: 'User invited!',
            });
          }).catch(() => {
            this.$store.commit('setGlobalAlert', {
              msg: 'Unable to save user. Please try again!', error: true,
            });
            whenDone();
          });
      }
    },
    showResetUser(user) {
      this.selectedUser = user;
      this.confirmTitle = 'Reset User';
      this.confirmBody = 'Are you sure you want to reset this user?';
      this.confirmAction = this.doResetUser;
      this.$refs.confirmModal.show();
    },
    deleteUser(user) {
      this.selectedUser = user;
      this.confirmTitle = 'Delete User';
      this.confirmBody = 'Are you sure you want to delete this user?';
      this.confirmAction = this.doDeleteUser;
      this.$refs.confirmModal.show();
    },
    getOrganizationSites(organizationId) {
      return this.userClient.getSitesForOrganization(organizationId);
    },
    resetMfa(user) {
      this.userClient.resetMfaForUser(user.id).then(() => {
        this.$store.commit('setGlobalAlert', {
          msg: 'User MFA count was successfully reset.',
        });
        // eslint-disable-next-line no-param-reassign
        user.loginsWithoutMfa = 0;
        // eslint-disable-next-line no-param-reassign
        user.hasMfaConfigured = false;
      }).catch(() => {
        this.$store.commit('setGlobalAlert', {
          msg: 'Unable to reset MFA count for user. Please try again.', error: true,
        });
      });
      return false;
    },
    doResetUser(whenDone) {
      if (this.selectedUser) {
        this.userClient.resetUser(this.selectedUser.id).then(() => {
          this.$refs.confirmModal.hide();
          this.getUsers();

          this.$store.commit('setGlobalAlert', {
            msg: 'User reset!',
          });
          this.selectedUser = null;
          whenDone();
        }).catch(() => {
          this.$store.commit('setGlobalAlert', {
            msg: 'Unable to reset user. Please try again!', error: true,
          });
          whenDone();
        });
        return false;
      }
      return true;
    },
    doDeleteUser(whenDone) {
      if (this.selectedUser) {
        this.userClient.deleteUser(this.selectedUser.id).then(() => {
          this.$refs.confirmModal.hide();
          this.getUsers();

          this.$store.commit('setGlobalAlert', {
            msg: 'User deleted!',
          });
          this.selectedUser = null;
          whenDone();
        }).catch(() => {
          this.$store.commit('setGlobalAlert', {
            msg: 'Unable to delete user. Please try again!', error: true,
          });
          whenDone();
        });
        return false;
      }
      return true;
    },
    deletePublicAccessKey(userId, accessKey) {
      return this.userClient.removeUserKeyForUser(userId, accessKey);
    },
  },
};
</script>

<style lang="scss" scoped>
.search-component {
  display: inline-block;
  width: calc(100% - 123px);
}

</style>
